import { useId } from "react";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

/**
 * Product scenes drawn in code with the site palette. Sample content (step names,
 * times, scores) is fictional and the frames are tagged as examples.
 */

export function QuestoveryScene({ className }: { className?: string }) {
	const { t } = useI18n();
	const screenId = `${useId().replace(/:/g, "")}-screen`;
	return (
		<svg
			viewBox="0 0 360 200"
			preserveAspectRatio="xMidYMid slice"
			className={cn("block h-full w-full", className)}
			aria-hidden="true"
		>
			<defs>
				<clipPath id={screenId}>
					<rect x="4" y="4" width="92" height="210" rx="13" />
				</clipPath>
			</defs>

			{/* map paper */}
			<rect width="360" height="200" style={{ fill: "var(--paper-3)" }} />
			<path
				d="M-10 196 C40 176 70 190 120 174 C160 162 196 180 240 166 L240 210 L-10 210 Z"
				style={{ fill: "var(--scene-water-2)" }}
			/>
			<path
				d="M-10 196 C40 176 70 190 120 174 C160 162 196 180 240 166"
				fill="none"
				strokeWidth="1.2"
				style={{ stroke: "var(--sky)" }}
			/>
			<ellipse
				cx="196"
				cy="38"
				rx="44"
				ry="26"
				style={{ fill: "var(--scene-park)" }}
			/>
			<g style={{ fill: "var(--scene-park-2)" }}>
				<circle cx="180" cy="34" r="5" />
				<circle cx="194" cy="44" r="4" />
				<circle cx="210" cy="32" r="5.5" />
				<circle cx="214" cy="48" r="3.5" />
			</g>

			{/* contour lines */}
			<g
				fill="none"
				strokeOpacity=".16"
				strokeWidth="1"
				style={{ stroke: "var(--lilac-ink)" }}
			>
				<path d="M40 60 C60 30 120 28 132 58 C144 88 110 112 76 106 C40 100 24 84 40 60 Z" />
				<path d="M56 64 C68 46 110 44 118 62 C126 82 102 96 80 92 C56 88 46 78 56 64 Z" />
				<path d="M72 68 C78 58 100 58 104 68 C108 78 94 84 84 82 C72 80 66 76 72 68 Z" />
			</g>

			{/* streets */}
			<g fill="none" strokeLinecap="round">
				<g strokeWidth="10" style={{ stroke: "var(--scene-rule)" }}>
					<path d="M-10 128 C50 118 110 150 260 132" />
					<path d="M150 -10 C140 50 170 100 150 210" />
					<path d="M-10 30 C30 40 60 20 150 30" />
				</g>
				<g strokeWidth="7.5" style={{ stroke: "var(--scene-card)" }}>
					<path d="M-10 128 C50 118 110 150 260 132" />
					<path d="M150 -10 C140 50 170 100 150 210" />
					<path d="M-10 30 C30 40 60 20 150 30" />
				</g>
			</g>

			{/* compass */}
			<g transform="translate(24 52)">
				<circle
					r="11"
					style={{ fill: "var(--paper-2)", stroke: "var(--scene-rule-2)" }}
				/>
				<path d="M0 -7 L3 0 L0 7 L-3 0 Z" style={{ fill: "var(--ink)" }} />
				<path d="M0 -7 L3 0 L-3 0 Z" style={{ fill: "var(--sky-ink)" }} />
			</g>

			{/* route */}
			<path
				d="M38 162 C60 140 70 120 94 108 C120 96 128 140 156 142 C186 144 186 98 214 80"
				fill="none"
				strokeWidth="2"
				strokeLinecap="round"
				strokeDasharray="0.1 5.5"
				style={{ stroke: "var(--ink)" }}
			/>
			<path
				className="qv-route"
				pathLength={1}
				d="M38 162 C60 140 70 120 94 108 C120 96 128 140 156 142"
				fill="none"
				strokeWidth="2.4"
				strokeLinecap="round"
				style={{ stroke: "var(--lilac-ink)" }}
			/>

			{/* pins */}
			<g
				fontFamily="IBM Plex Mono"
				fontSize="7.5"
				fontWeight="600"
				textAnchor="middle"
			>
				<g transform="translate(38 162)">
					<path
						d="M0 0 C-6 -8 -9 -12 -9 -17 a9 9 0 1 1 18 0 c0 5 -3 9 -9 17 Z"
						style={{ fill: "var(--ink)" }}
					/>
					<text y="-14" style={{ fill: "var(--paper-2)" }}>
						1
					</text>
				</g>
				<g transform="translate(94 108)">
					<path
						d="M0 0 C-6 -8 -9 -12 -9 -17 a9 9 0 1 1 18 0 c0 5 -3 9 -9 17 Z"
						style={{ fill: "var(--ink)" }}
					/>
					<text y="-14" style={{ fill: "var(--paper-2)" }}>
						2
					</text>
				</g>
				<g transform="translate(156 142)">
					<circle
						className="qv-pulse scene-box"
						r="13"
						opacity=".18"
						style={{ fill: "var(--sky-ink)" }}
					/>
					<path
						d="M0 0 C-7 -9 -10.5 -14 -10.5 -19.5 a10.5 10.5 0 1 1 21 0 c0 5.5 -3.5 10.5 -10.5 19.5 Z"
						style={{ fill: "var(--sky-ink)" }}
					/>
					<text y="-16.5" style={{ fill: "var(--paper-2)" }}>
						3
					</text>
				</g>
				<g transform="translate(214 80)">
					<circle r="3" style={{ fill: "var(--ink)" }} />
					<line
						x1="0"
						y1="0"
						x2="0"
						y2="-24"
						strokeWidth="1.4"
						style={{ stroke: "var(--ink)" }}
					/>
					<path
						d="M0 -24 h14 l-4 5 l4 5 h-14 Z"
						style={{ fill: "var(--lilac-ink)" }}
					/>
				</g>
			</g>

			{/* team bubble */}
			<g transform="translate(167 148)">
				<g className="qv-float">
					<rect
						width="66"
						height="20"
						rx="10"
						style={{ fill: "var(--paper-2)", stroke: "var(--scene-rule)" }}
					/>
					<circle cx="10" cy="10" r="5.5" style={{ fill: "var(--sky-ink)" }} />
					<circle
						cx="16"
						cy="10"
						r="5.5"
						strokeWidth="1"
						style={{ fill: "var(--lilac-ink)", stroke: "var(--paper-2)" }}
					/>
					<text
						x="26"
						y="12.6"
						fontFamily="IBM Plex Sans"
						fontSize="7"
						fontWeight="600"
						style={{ fill: "var(--ink)" }}
					>
						{t((t) => t.scenes.questovery.team)}
					</text>
				</g>
			</g>

			{/* participant phone */}
			<g transform="translate(250 14)">
				<rect
					x="3"
					y="5"
					width="100"
					height="210"
					rx="17"
					opacity=".1"
					style={{ fill: "var(--ink)" }}
				/>
				<rect width="100" height="210" rx="17" style={{ fill: "var(--ink)" }} />
				<g clipPath={`url(#${screenId})`}>
					<rect
						x="4"
						y="4"
						width="92"
						height="210"
						style={{ fill: "var(--paper-2)" }}
					/>
					<rect
						x="38"
						y="9"
						width="24"
						height="7"
						rx="3.5"
						style={{ fill: "var(--ink)" }}
					/>
					<text
						x="12"
						y="31"
						fontFamily="IBM Plex Mono"
						fontSize="5.6"
						letterSpacing=".08em"
						style={{ fill: "var(--ink-4)" }}
					>
						{t((t) => t.scenes.questovery.step).toUpperCase()}
					</text>
					<text
						x="88"
						y="31"
						fontFamily="IBM Plex Mono"
						fontSize="5.6"
						textAnchor="end"
						style={{ fill: "var(--lilac-ink)" }}
					>
						{t((t) => t.scenes.questovery.points)}
					</text>
					<text
						x="12"
						y="43"
						fontFamily="IBM Plex Sans"
						fontSize="9"
						fontWeight="600"
						style={{ fill: "var(--ink)" }}
					>
						{t((t) => t.scenes.questovery.title)}
					</text>
					<rect
						x="12"
						y="49"
						width="76"
						height="3"
						rx="1.5"
						style={{ fill: "var(--scene-rule)" }}
					/>
					<rect
						x="12"
						y="49"
						width="46"
						height="3"
						rx="1.5"
						style={{ fill: "var(--lilac-ink)" }}
					/>

					<rect
						x="12"
						y="60"
						width="76"
						height="76"
						rx="9"
						strokeWidth=".8"
						style={{ fill: "var(--scene-card)", stroke: "var(--scene-rule)" }}
					/>
					<g transform="translate(29 77)" style={{ fill: "var(--ink)" }}>
						<path
							d="M0 0h12v12H0z M3 3v6h6V3z M30 0h12v12H30z M33 3v6h6V3z M0 30h12v12H0z M33 33v6h6v-6z"
							fillRule="evenodd"
						/>
						<rect x="4.5" y="4.5" width="3" height="3" />
						<rect x="34.5" y="4.5" width="3" height="3" />
						<rect x="4.5" y="34.5" width="3" height="3" />
						<rect x="15" y="0" width="3" height="3" />
						<rect x="21" y="0" width="3" height="6" />
						<rect x="15" y="6" width="6" height="3" />
						<rect x="24" y="9" width="3" height="3" />
						<rect x="15" y="12" width="3" height="6" />
						<rect x="0" y="15" width="6" height="3" />
						<rect x="9" y="15" width="3" height="6" />
						<rect x="21" y="15" width="6" height="3" />
						<rect x="30" y="15" width="3" height="3" />
						<rect x="36" y="15" width="6" height="3" />
						<rect x="3" y="21" width="3" height="3" />
						<rect x="15" y="21" width="3" height="3" />
						<rect x="24" y="21" width="9" height="3" />
						<rect x="39" y="21" width="3" height="6" />
						<rect x="0" y="24" width="3" height="3" />
						<rect x="9" y="24" width="6" height="3" />
						<rect x="18" y="27" width="3" height="6" />
						<rect x="27" y="27" width="3" height="3" />
						<rect x="33" y="27" width="3" height="3" />
						<rect x="15" y="33" width="3" height="3" />
						<rect x="21" y="36" width="6" height="3" />
						<rect x="30" y="30" width="3" height="3" />
						<rect x="15" y="39" width="3" height="3" />
						<rect x="27" y="39" width="3" height="3" />
						<rect x="36" y="27" width="3" height="3" />
					</g>
					<g
						fill="none"
						strokeWidth="2"
						strokeLinecap="round"
						style={{ stroke: "var(--sky-ink)" }}
					>
						<path d="M22 78 v-8 h8 M78 78 v-8 h-8 M22 118 v8 h8 M78 118 v8 h-8" />
					</g>
					<line
						className="qv-scan"
						x1="20"
						y1="98"
						x2="80"
						y2="98"
						strokeWidth="1"
						strokeOpacity=".7"
						style={{ stroke: "var(--sky-ink)" }}
					/>

					<text
						x="50"
						y="148"
						fontFamily="IBM Plex Sans"
						fontSize="5.7"
						textAnchor="middle"
						style={{ fill: "var(--ink-2)" }}
					>
						{t((t) => t.scenes.questovery.scan)}
					</text>
					<rect
						x="12"
						y="155"
						width="76"
						height="17"
						rx="8.5"
						style={{ fill: "var(--lilac-ink)" }}
					/>
					<text
						x="50"
						y="166"
						fontFamily="IBM Plex Sans"
						fontSize="7.4"
						fontWeight="600"
						textAnchor="middle"
						style={{ fill: "var(--paper-2)" }}
					>
						{t((t) => t.scenes.questovery.validate)}
					</text>
				</g>
			</g>
		</svg>
	);
}

export function ChabanScene({ className }: { className?: string }) {
	const { t } = useI18n();
	const id = useId().replace(/:/g, "");
	return (
		<svg
			viewBox="0 28 360 166"
			preserveAspectRatio="xMidYMid slice"
			className={cn("block h-full w-full", className)}
			aria-hidden="true"
		>
			<defs>
				<linearGradient id={`${id}-sky`} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" style={{ stopColor: "var(--scene-sky-top)" }} />
					<stop offset=".75" style={{ stopColor: "var(--scene-paper-warm)" }} />
				</linearGradient>
				<linearGradient id={`${id}-water`} x1="0" y1="0" x2="0" y2="1">
					<stop offset="0" style={{ stopColor: "var(--scene-water)" }} />
					<stop offset="1" style={{ stopColor: "var(--sky)" }} />
				</linearGradient>
				<linearGradient id={`${id}-pylon`} x1="0" y1="0" x2="1" y2="0">
					<stop offset="0" style={{ stopColor: "var(--scene-steel)" }} />
					<stop offset="1" style={{ stopColor: "var(--ink)" }} />
				</linearGradient>
			</defs>

			<rect width="360" height="200" fill={`url(#${id}-sky)`} />
			<circle
				cx="64"
				cy="92"
				r="30"
				style={{ fill: "var(--scene-lilac-wash-2)" }}
			/>
			<circle cx="64" cy="92" r="20" style={{ fill: "var(--lilac)" }} />

			{/* far bank skyline */}
			<path
				d="M0 150 h18 v-10 h10 v6 h12 v-14 h8 v14 h14 v-8 h10 v12 h14 V150 Z M250 150 h12 v-12 h10 v6 h16 v-16 h8 v16 h12 v-6 h14 v10 h12 v-4 h26 V150 Z"
				style={{ fill: "var(--scene-skyline)" }}
			/>

			{/* water */}
			<rect y="158" width="360" height="42" fill={`url(#${id}-water)`} />
			<g
				strokeOpacity=".7"
				strokeWidth="1.1"
				strokeLinecap="round"
				fill="none"
				style={{ stroke: "var(--paper-2)" }}
			>
				<path d="M14 170 h22 M58 176 h14 M92 168 h18 M262 172 h26 M306 180 h18 M330 168 h14 M30 188 h30 M286 192 h22" />
			</g>
			<g
				strokeOpacity=".18"
				strokeWidth="1"
				strokeLinecap="round"
				style={{ stroke: "var(--ink)" }}
			>
				<path d="M152 176 h56 M158 182 h44 M166 188 h28" />
			</g>

			{/* approach viaducts */}
			<g style={{ fill: "var(--scene-steel)" }}>
				<rect x="-4" y="132" width="114" height="6" />
				<rect x="26" y="138" width="5" height="20" />
				<rect x="66" y="138" width="5" height="20" />
				<rect x="250" y="132" width="114" height="6" />
				<rect x="289" y="138" width="5" height="20" />
				<rect x="329" y="138" width="5" height="20" />
			</g>
			<g
				strokeWidth="1"
				strokeDasharray="2 2"
				style={{ stroke: "var(--sky-ink)" }}
			>
				<line x1="0" y1="129" x2="106" y2="129" />
				<line x1="254" y1="129" x2="360" y2="129" />
			</g>

			{/* deck's resting position */}
			<rect
				x="124"
				y="132"
				width="112"
				height="6"
				fill="none"
				strokeOpacity=".55"
				strokeDasharray="3 2.5"
				style={{ stroke: "var(--lilac-ink)" }}
			/>
			<g
				fill="none"
				strokeWidth="1.2"
				strokeLinecap="round"
				strokeLinejoin="round"
				style={{ stroke: "var(--lilac-ink)" }}
			>
				<path d="M150 124 V100 M146 104 l4 -4 l4 4" />
				<path d="M210 124 V100 M206 104 l4 -4 l4 4" />
			</g>

			{/* pylons (back pair, then front pair) */}
			<g style={{ fill: "var(--scene-steel-2)" }}>
				<path d="M118 158 L120 46 L128 46 L131 158 Z" />
				<path d="M238 158 L240 46 L248 46 L251 158 Z" />
			</g>
			<g fill={`url(#${id}-pylon)`}>
				<path d="M108 158 L111 40 L121 40 L124 158 Z" />
				<path d="M228 158 L231 40 L241 40 L244 158 Z" />
				<rect x="107" y="34" width="18" height="9" rx="2" />
				<rect x="227" y="34" width="18" height="9" rx="2" />
			</g>
			<g style={{ fill: "var(--sky-ink)" }}>
				<circle cx="116" cy="31" r="2" />
				<circle cx="236" cy="31" r="2" />
			</g>

			{/* lifted span + cables */}
			<g className="ch-deck">
				<g strokeWidth=".7" strokeOpacity=".6" style={{ stroke: "var(--ink)" }}>
					<line x1="121" y1="44" x2="124" y2="64" />
					<line x1="231" y1="44" x2="236" y2="64" />
				</g>
				<rect
					x="122"
					y="64"
					width="116"
					height="7"
					rx="1"
					style={{ fill: "var(--ink)" }}
				/>
				<g
					strokeOpacity=".35"
					strokeWidth=".6"
					style={{ stroke: "var(--paper-2)" }}
				>
					<path d="M130 67.5 h100" />
				</g>
			</g>

			{/* tall ship passing under */}
			<g className="ch-ship">
				<path
					d="M144 150 L216 150 L208 161 L152 161 Z"
					style={{ fill: "var(--ink)" }}
				/>
				<rect
					x="149"
					y="147"
					width="62"
					height="3"
					style={{ fill: "var(--lilac-ink)" }}
				/>
				<g strokeWidth="1.2" style={{ stroke: "var(--ink)" }}>
					<line x1="164" y1="147" x2="164" y2="80" />
					<line x1="184" y1="147" x2="184" y2="76" />
					<line x1="202" y1="147" x2="202" y2="86" />
				</g>
				<g
					strokeWidth=".7"
					strokeLinejoin="round"
					style={{ fill: "var(--paper-2)", stroke: "var(--ink)" }}
				>
					<path d="M165 86 Q175 98 165 110 Z" />
					<path d="M165 114 Q177 127 165 140 Z" />
					<path d="M185 82 Q197 95 185 108 Z" />
					<path d="M185 112 Q199 127 185 142 Z" />
					<path d="M203 92 Q212 104 203 116 Z" />
					<path d="M203 120 Q213 131 203 142 Z" />
				</g>
				<path d="M184 76 l9 3 l-9 3 Z" style={{ fill: "var(--sky-ink)" }} />
			</g>

			{/* status pill */}
			<g transform="translate(14 48)">
				<rect
					width="78"
					height="20"
					rx="10"
					style={{ fill: "var(--paper-2)", stroke: "var(--scene-rule)" }}
				/>
				<circle cx="12" cy="10" r="3.4" style={{ fill: "var(--sky-ink)" }} />
				<circle
					cx="12"
					cy="10"
					r="6"
					opacity=".18"
					style={{ fill: "var(--sky-ink)" }}
				/>
				<text
					x="22"
					y="12.8"
					fontFamily="IBM Plex Mono"
					fontSize="7"
					fontWeight="500"
					letterSpacing=".06em"
					style={{ fill: "var(--ink)" }}
				>
					{t((t) => t.scenes.chaban.status).toUpperCase()}
				</text>
			</g>

			{/* push notification */}
			<g transform="translate(166 44)">
				<g className="ch-notif">
					<rect
						x="1"
						y="4"
						width="180"
						height="50"
						rx="11"
						opacity=".08"
						style={{ fill: "var(--ink)" }}
					/>
					<rect
						width="180"
						height="50"
						rx="11"
						strokeWidth=".8"
						style={{ fill: "var(--scene-card)", stroke: "var(--scene-rule)" }}
					/>
					<rect
						x="9"
						y="10"
						width="20"
						height="20"
						rx="5.5"
						style={{ fill: "var(--lilac-ink)" }}
					/>
					<g
						strokeWidth="1.5"
						strokeLinecap="round"
						style={{ stroke: "var(--paper-2)" }}
					>
						<path d="M14.5 26 V14.5 M23.5 26 V14.5" />
						<path d="M13 20 h12" />
					</g>
					<text
						x="36"
						y="17"
						fontFamily="IBM Plex Mono"
						fontSize="6"
						letterSpacing=".06em"
						style={{ fill: "var(--ink-4)" }}
					>
						{t((t) => t.scenes.chaban.app).toUpperCase()}
					</text>
					<text
						x="171"
						y="17"
						fontFamily="IBM Plex Mono"
						fontSize="6"
						textAnchor="end"
						style={{ fill: "var(--ink-4)" }}
					>
						{t((t) => t.scenes.chaban.time)}
					</text>
					<text
						x="36"
						y="29.5"
						fontFamily="IBM Plex Sans"
						fontSize="9"
						fontWeight="600"
						style={{ fill: "var(--ink)" }}
					>
						{t((t) => t.scenes.chaban.title)}
					</text>
					<text
						x="36"
						y="40.5"
						fontFamily="IBM Plex Sans"
						fontSize="6.9"
						style={{ fill: "var(--ink-2)" }}
					>
						{t((t) => t.scenes.chaban.body)}
					</text>
				</g>
			</g>
		</svg>
	);
}
